import {BORDER, NAVY, SUBTLE, MUTED, WARNING, withOpacity} from '../../../styles/foundation/colors'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'

/**
 * Table chrome shared by the module's two data tables — DisputeGrid's ledger
 * and SkuIntelligence's weight corrections. Extracted so the second table
 * cannot quietly drift from the first: the dividers were added to the grid
 * first and simply never reached the SKU table, which is exactly the kind of
 * gap a shared constant prevents.
 *
 * Vertical column rules are a local decision, not an app-wide one. Every other
 * modern table in this panel separates rows only — both shared table styles in
 * styles/patterns carry a borderBottom and nothing else — so this stays here
 * rather than moving into patterns/index.js.
 *
 * Half-strength BORDER rather than the full token: the horizontals are already
 * solid BORDER on every row, and a vertical of equal weight turns the grid into
 * a spreadsheet. That is the failure mode of the legacy `1px solid #ccc` tables
 * under dashboard/performance, whose verticals are darker than their
 * horizontals. It must stay an alpha rather than a baked hex — rows render
 * `hover` and `selected`, which paint a tint behind the cells, and a flat light
 * hex would punch a visible pale stripe through every highlighted row.
 */
export const COLUMN_RULE = `1px solid ${withOpacity(BORDER, 0.5)}`

/**
 * 8px each side instead of the shared pattern's 12px. Across eleven columns
 * that is 88px off a table's minimum width, and it costs nothing legible
 * because the rules above now do the job the wide gutters were doing.
 */
export const CELL_PX = 1

/**
 * Spread onto a `<Table sx>`, alongside that table's own minWidth. Applied at
 * table level rather than per cell so an individual TableCell cannot miss it.
 * The trailing edge is excluded — a rule there would only double the table's
 * own border. Tables with a leading checkbox column should additionally clear
 * `:first-of-type`, since that column belongs to the row rather than the data.
 */
export const columnDividerSx = {
  '& th, & td': {borderRight: COLUMN_RULE, px: CELL_PX},
  '& th:last-of-type, & td:last-of-type': {borderRight: 'none'},
}

/* ── Weight figures ───────────────────────────────────────────────
 * All at SIZE['2xs']. The module used to run two body tiers one step apart —
 * 12px on 124 elements and 13.12px on 50 — a difference too small to read as
 * hierarchy but large enough to cost a few pixels on every stacked cell.
 * Weight, not size, separates the figures that matter: Billed is bold, the
 * declared side is not.
 * Shared by the ledger grid and the triage view so the same number never
 * renders two ways on two screens. These started as locals in DisputeGrid;
 * they moved here the moment a second surface needed them, for the same
 * reason columnDividerSx did — a copy is a copy until it silently isn't.
 */

/** The Declared column: the merchant's own figure, deliberately quiet. */
export const declaredFigureSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}

/** The Billed headline: what the courier actually charged, and the loudest
 *  figure in the row because it is the one being contested. */
export const billedFigureSx = {fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE['2xs'], color: NAVY}

/**
 * The two Excess figures, stepped down to SIZE['2xs'] because "+0.25 kg" needs
 * 57px on one line while its column's content box is 51px — so it broke after
 * the number and dropped "kg" onto a second line. The size drop alone does not
 * fix it (12px still needs 52px); nowrap is what guarantees a single line, by
 * making the string the column's min-content so the column widens instead.
 */
export const excessFigureSx = {
  fontFamily: FONT,
  fontWeight: WEIGHT.semibold,
  fontSize: SIZE['2xs'],
  color: NAVY,
  whiteSpace: 'nowrap',
}

export const subtleTextSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}

/**
 * The l×b×h line under a weight breakdown. Dimensions read as a spec, not
 * prose — tabular figures keep the digits from jittering between rows, which
 * matters when every row shows a different triple.
 *
 * nowrap because the Billed column is only ~80px of usable width once cell
 * padding is taken off, and "37×26×3.22 cm" lands just inside that — so it
 * broke before "cm" and cost 17px of height on EVERY row. Measured on the
 * harness, not guessed.
 *
 * Started as a local in DisputeGrid; it moved here the moment SkuIntelligence's
 * Merchant weight cell needed the same line, for the same reason
 * declaredFigureSx and columnDividerSx did.
 */
export const dimsSx = {
  fontFamily: FONT,
  fontSize: '11px',
  color: MUTED,
  fontVariantNumeric: 'tabular-nums',
  mt: 0.25,
  whiteSpace: 'nowrap',
}

/** The governing side of the dead-vs-volumetric pair. */
export const governSx = {fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: NAVY}

/** DEAD / VOL, as small uppercase prefixes beside their figures. */
export const prefixSx = {
  fontFamily: FONT,
  fontSize: '11px',
  fontWeight: WEIGHT.semibold,
  color: MUTED,
  // A two-word label is allowed to be narrow, never broken. Without this the
  // Resolved tab -- the widest, because it alone carries a Status column -- had
  // the least room left for these cells and split "Dead Wt." across two lines,
  // which reads as two labels rather than one.
  whiteSpace: 'nowrap',
  letterSpacing: '0.04em',
}

/**
 * The governing prefix. An amber TINT behind NAVY text, not amber text —
 * amber-on-white measured 1.51:1 at this size, far under the 4.5:1 floor.
 */
export const governPrefixSx = {
  ...prefixSx,
  color: NAVY,
  bgcolor: withOpacity(WARNING, 0.22),
  borderRadius: '4px',
  px: 0.5,
}
